import { AlertOctagon, CheckCircle2, Clock, ShieldAlert, Undo, XCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { SaleStatus } from '@shared/contracts/enums'
import type { StatusTone } from '@shared/design-system/ui'
import type { SaleListItem } from './sale.contract'
import type { SaleFinancialEvent, SaleFunnelStage } from './sale.contract'

export type SaleStatusPresentation = {
  tone: StatusTone
  label: string
  icon: LucideIcon
}

const presentationByStatus: Record<SaleStatus, SaleStatusPresentation> = {
  PENDING: { tone: 'awaiting', label: 'Pendente', icon: Clock },
  PAID: { tone: 'success', label: 'Paga', icon: CheckCircle2 },
  FAILED: { tone: 'closed', label: 'Recusada', icon: XCircle },
  REFUND_REQUESTED: { tone: 'awaiting', label: 'Aguardando reembolso', icon: Undo },
  REFUNDED: { tone: 'informative', label: 'Estorno', icon: Undo },
  CHARGED_BACK: { tone: 'closed', label: 'Chargeback', icon: AlertOctagon },
}

const globalChargebackRefundPresentation: SaleStatusPresentation = {
  tone: 'closed',
  label: 'Reembolso de chargeback',
  icon: ShieldAlert,
}

type SaleStatusPresentationInput =
  | SaleStatus
  | Pick<SaleListItem, 'status' | 'handledByGlobalChargeback'>

export function getSaleStatusPresentation(
  input: SaleStatusPresentationInput,
): SaleStatusPresentation {
  const status = typeof input === 'string' ? input : input.status
  const handledByGlobalChargeback =
    typeof input === 'string' ? false : Boolean(input.handledByGlobalChargeback)

  if (status === 'REFUNDED' && handledByGlobalChargeback) {
    return globalChargebackRefundPresentation
  }

  return presentationByStatus[status]
}

type ChargeStatusInput = Pick<SaleListItem, 'status'> &
  Partial<
    Pick<SaleListItem, 'handledByGlobalChargeback' | 'subscriptionStatus' | 'financialEvents'>
  >

export function getChargeStatusPresentation(
  input: ChargeStatusInput | SaleFunnelStage,
): SaleStatusPresentation {
  const latest: SaleFinancialEvent | undefined = input.financialEvents
    ?.filter((event) => event.kind !== 'FX_LOSS')
    .at(-1)
  if (latest?.kind === 'PARTIAL_REFUND') {
    return { tone: 'informative', label: 'Reembolso parcial', icon: Undo }
  }
  if (latest?.kind === 'DISPUTE_WON' && input.status !== 'CHARGED_BACK') {
    return { tone: 'success', label: 'Disputa ganha', icon: CheckCircle2 }
  }
  if (input.status === 'PAID' && input.subscriptionStatus === 'CANCELED') {
    return { tone: 'closed', label: 'Assinatura encerrada', icon: XCircle }
  }
  return getSaleStatusPresentation(input)
}

export function getJourneyStatusPresentation(
  input: ChargeStatusInput & { funnelStages?: ChargeStatusInput[] },
): SaleStatusPresentation {
  const statuses = [input, ...(input.funnelStages ?? [])].map(getChargeStatusPresentation)
  return new Set(statuses.map((status) => status.label)).size > 1
    ? { tone: 'informative', label: 'Situação mista', icon: AlertOctagon }
    : statuses[0]!
}
